import React, { useEffect, useRef, useState } from "react";
import './AppContactUs.css';
import './AppContactUs-responsive.css';
import { ProgressSpinner } from "primereact/progressspinner";
import ErrorPage from "../../Components/ErrorPage";
import TextInput from "../../Components/TextInput";
import Textarea from "../../Components/TextArea";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { goToTop } from "../../Components/goToTop";
import { addContactUsApi } from "../../Api/SignupApi";
import { checkEmail, checkText } from "../../Utils/Validator";

const AppContactUs: React.FC = () => {
    const contactError = '';
    const loadingContact = false;
    const toast = useRef<Toast>(null);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');

    const [loading, setLoading] = useState<boolean>(false);
    const [contactName, setContactName] = useState<string>('');
    const [contactEmail, setContactEmail] = useState<string>('');
    const [contactMessage, setContactMessage] = useState<string>('');
    const [contactNameError, setContactNameError] = useState<string>('');
    const [contactEmailError, setContactEmailError] = useState<string>('');
    const [contactMessageError, setContactMessageError] = useState<string>('');

    const handleSendContactMessage = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials()) {
            setLoading(true);
            const res = await addContactUsApi(contactName, contactEmail, contactMessage)
            setLoading(false);

            if (res.status) {
                setSuccessMessage('Message sent successfully. We will contact you shortly.');
                setShowAlert(true);
                handleClearFields();
                goToTop();
                setTimeout(() => {
                    setSuccessMessage('');
                    setShowAlert(false);
                }, 2000);
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error sending message.',
                        detail: 'There was an error sending message.',
                        life: 3000
                    });
                }
            }
        }
    }

    useEffect(() => {
        checkText(contactName, false, setContactNameError);
    }, [contactName]);

    useEffect(() => {
        checkEmail(contactEmail, false, setContactEmailError);
    }, [contactEmail]);

    useEffect(() => {
        checkText(contactMessage, false, setContactMessageError);
    }, [contactMessage]);

    function checkCredentials(): boolean {
        const contactNameValid = checkText(contactName, true, setContactNameError);
        const contactEmailValid = checkEmail(contactEmail, true, setContactEmailError);
        const contactMessageValid = checkText(contactMessage, true, setContactMessageError);

        return contactNameValid && contactEmailValid && contactMessageValid;
    }

    const handleClearFields = () => {
        setContactName('');
        setContactEmail('');
        setContactMessage('');
        setContactNameError('');
        setContactEmailError('');
        setContactMessageError('');
    }

    const handleCloseAlert = () => {
        setShowAlert(false);
    };

    return (
        <React.Fragment>
            <div className="main_content layout_padding show_side_bar">

                <Toast ref={toast} />

                {loadingContact ? (
                    <div className="loading_container">
                        <ProgressSpinner className="progresscircle custom" style={{ width: '50px', height: '50px', stroke: '#8953D7' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration="1s" />
                    </div>
                ) : contactError ? (
                    <ErrorPage errorMessage={contactError} />
                ) : (
                    <div>
                        <h4 className="main_heading">Contact Us</h4>

                        <div className="page_content">
                            <section className="page_section contact_us_section">
                                <div className="contact_us_container">
                                    <div className="row">
                                        <div className="col-12 col-xl-5 mx-auto">
                                            <div className="contact_img_area">
                                                <h5>Leave us a message</h5>
                                                <img
                                                    src="/assets/images/contact_us_img.svg"
                                                    alt="Contact us"
                                                    draggable={false}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-12 col-xl-7 mx-auto">
                                            <div className="contact_form_area">
                                                <div className="row">
                                                    <div className="col-12 col-sm-6 col-xl-12">
                                                        <TextInput
                                                            id="contactName"
                                                            label=""
                                                            labelHtmlFor="contactName"
                                                            required={true}
                                                            value={contactName}
                                                            placeholder="Enter your name *"
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContactName(e.target.value)}
                                                            error={contactNameError}
                                                            inputAutoFocus={true}
                                                        />
                                                    </div>

                                                    <div className="col-12 col-sm-6 col-xl-12">
                                                        <TextInput
                                                            id="contactEmail"
                                                            label=""
                                                            labelHtmlFor="contactEmail"
                                                            required={true}
                                                            value={contactEmail}
                                                            placeholder="Enter your email *"
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContactEmail(e.target.value)}
                                                            error={contactEmailError}
                                                        />
                                                    </div>

                                                    {/* Description */}
                                                    <div className="col-12">
                                                        <Textarea
                                                            id="contactMessage"
                                                            label=""
                                                            labelHtmlFor="contactMessage"
                                                            required={true}
                                                            value={contactMessage}
                                                            placeholder="Enter your message *"
                                                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setContactMessage(e.target.value)}
                                                            error={contactMessageError}
                                                            formGroupClassName="mb-0"
                                                            textAreaClassName="contact_text_area"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="contact_btn_area">
                                                    <Button label={`${loading ? 'Processing' : 'Save'}`}
                                                        onClick={handleSendContactMessage}
                                                        loading={loading} className="custom_btn primary contact_send_btn"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                )}
            </div>

            {/* Show success message */}
            <div className={`message_backdrop ${showAlert ? 'show' : ''}`} onClick={handleCloseAlert}>
                <div className={`message_box ${showAlert ? 'show' : ''}`} onClick={e => e.stopPropagation()}>
                    <div className="message_box_content">
                        <i className="bi bi-check-circle-fill message_box_icon"></i>
                        <h6 className="message_box_message">
                            {successMessage}
                        </h6>
                    </div>
                </div>
            </div>
            {/*  */}
        </React.Fragment>
    )
}

export default AppContactUs;